import { FullStory } from '@fullstory/browser'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { userRoles } from 'src/constants/auth.constants'
import { getEnvVar, getEnvVarWithDefault } from 'src/lib/env'
import { UserInfo } from 'src/types/plan.interface'

// TODO, this interface should be deprecated and instead we should use AccountInfo IdTokenClaims interface from @azure/msal-browser
export interface IdTokenClaims extends Omit<UserInfo, 'groups'> {
  aio: string
  aud: string
  exp: number
  groups?: string[]
  iat: number
  iss: string
  name: string
  nbf: number
  nonce: string
  oid: string
  preferred_username: string
  rh: string
  sub: string
  tid: string
  userdivision: string
  userregion: string
  uti: string
  ver: string
}
export interface AuthState {
  isAuthenticated: boolean
  isCorporateCreateUser: boolean
  isCorporateViewUser: boolean
  isDivisionCreateUser: boolean
  isDivisionViewUser: boolean
  readableUserRoles: string[]
  userInfo: UserInfo
}

const authState: AuthState = {
  isAuthenticated: false,
  isCorporateCreateUser: false,
  isCorporateViewUser: false,
  isDivisionCreateUser: false,
  isDivisionViewUser: false,
  readableUserRoles: [],
  userInfo: {} as UserInfo,
}

const roles = [
  getEnvVarWithDefault('REACT_APP_PLAN_LIBRARY_CORPORATE_CREATE_USER', ''),
  getEnvVarWithDefault('REACT_APP_PLAN_LIBRARY_DIVISION_CREATE_USER', ''),
  getEnvVarWithDefault('REACT_APP_PLAN_LIBRARY_CORPORATE_VIEW_USER', ''),
  getEnvVarWithDefault('REACT_APP_PLAN_LIBRARY_DIVISION_VIEW_USER', ''),
]

const rolesFriendlyNames = {
  [getEnvVarWithDefault('REACT_APP_PLAN_LIBRARY_CORPORATE_CREATE_USER', '')]:
    userRoles.CORPORATE_CREATE,
  [getEnvVarWithDefault('REACT_APP_PLAN_LIBRARY_CORPORATE_VIEW_USER', '')]:
    userRoles.CORPORATE_VIEW,
  [getEnvVarWithDefault('REACT_APP_PLAN_LIBRARY_DIVISION_CREATE_USER', '')]:
    userRoles.DIVISION_CREATE,
  [getEnvVarWithDefault('REACT_APP_PLAN_LIBRARY_DIVISION_VIEW_USER', '')]:
    userRoles.DIVISION_VIEW,
}

export const AuthSlice = createSlice({
  initialState: authState,
  name: 'Auth',
  reducers: {
    loadUserInfoFromSession: (state) => {
      const storedUserInfo = sessionStorage.getItem('userInfo')
      if (storedUserInfo) {
        state.userInfo = JSON.parse(storedUserInfo)?.userInfo
        state.isCorporateViewUser =
          JSON.parse(storedUserInfo)?.isCorporateViewUser
        state.isCorporateCreateUser =
          JSON.parse(storedUserInfo)?.isCorporateCreateUser
        state.isDivisionCreateUser =
          JSON.parse(storedUserInfo)?.isDivisionCreateUser
        state.isDivisionViewUser =
          JSON.parse(storedUserInfo)?.isDivisionViewUser
      }
    },
    setAuthState: (
      state,
      action: PayloadAction<{ isAuthenticated: boolean }>
    ) => {
      state.isAuthenticated = action.payload.isAuthenticated
    },
    setUserInfo: (
      state,
      action: PayloadAction<{
        account: { idTokenClaims: IdTokenClaims }
        email: string
      }>
    ) => {
      const idTokenClaims = action.payload.account.idTokenClaims
      // filter out the roles which are not specific to plan library
      const environmentSpecificPlanLibraryGroups = idTokenClaims.groups?.filter(
        (group) => roles.includes(group)
      )

      state.readableUserRoles =
        (environmentSpecificPlanLibraryGroups &&
          environmentSpecificPlanLibraryGroups.map(
            (role) => rolesFriendlyNames[role] || role
          )) ||
        []

      const selectedRole = roles.find(
        (role) => environmentSpecificPlanLibraryGroups?.includes(role)
      )

      const isCorporateCreateUser =
        selectedRole ===
        (getEnvVar('REACT_APP_PLAN_LIBRARY_CORPORATE_CREATE_USER') ?? '')

      const isCorporateViewUser =
        selectedRole ===
        (getEnvVar('REACT_APP_PLAN_LIBRARY_CORPORATE_VIEW_USER') ?? '')

      const isDivisionCreateUser =
        selectedRole ===
        (getEnvVar('REACT_APP_PLAN_LIBRARY_DIVISION_CREATE_USER') ?? '')

      const isDivisionViewUser =
        selectedRole ===
        (getEnvVar('REACT_APP_PLAN_LIBRARY_DIVISION_VIEW_USER') ?? '')

      const getRole = () => {
        if (isCorporateCreateUser) return 'Corporate Create'
        if (isCorporateViewUser) return 'Corporate View'
        if (isDivisionCreateUser) return 'Division Create'
        if (isDivisionViewUser) return 'Division View'
        return 'unknown'
      }

      FullStory('setProperties', {
        properties: {
          division: idTokenClaims?.userdivision,
          region: idTokenClaims?.userregion,
          role: getRole(),
          userId: idTokenClaims?.oid,
        },
        type: 'user',
      })

      state.userInfo = {
        ...idTokenClaims,
        email: action.payload.email?.toLowerCase(),
        groups: selectedRole ? [selectedRole] : [],
      }
      sessionStorage.setItem(
        'userInfo',
        JSON.stringify({
          isCorporateCreateUser,
          isCorporateViewUser,
          isDivisionCreateUser,
          isDivisionViewUser,
          userInfo: {
            ...idTokenClaims,
            email: action.payload.email?.toLowerCase(),
            groups: selectedRole ? [selectedRole] : [],
          },
        })
      )
      state.isCorporateViewUser = isCorporateViewUser
      state.isCorporateCreateUser = isCorporateCreateUser
      state.isDivisionCreateUser = isDivisionCreateUser
      state.isDivisionViewUser = isDivisionViewUser
    },
  },
})

export const { loadUserInfoFromSession, setAuthState, setUserInfo } =
  AuthSlice.actions
